import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Login,
  Logout,
  PosthogEvent,
  Register,
  RegistrationData,
  SessionModelResponse,
  User
} from '@core/models';
import {
  AbTestsService,
  API_LIST,
  CartService,
  GetUserEngageKey,
  GonativeService,
  GtmService,
  PosthogService,
  UserengageService
} from '@core/services';
import { Store } from '@ngxs/store';
import { ClearAuthData, SetSession } from '@store/auth/auth.actions';
import { Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private httpClient: HttpClient,
    private store: Store,
    private window: Window,
    private cartService: CartService,
    private gtm: GtmService,
    private abTestsService: AbTestsService,
    private userEngage: UserengageService,
    private router: Router,
    private posthogService: PosthogService
  ) {}

  public login(
    email_or_username: string,
    password: string,
    clearBasket: boolean = false,
    comesFromRegister: boolean = false
  ) {
    return this.httpClient
      .post<SessionModelResponse>(API_LIST.login(clearBasket), {
        email_or_username,
        password
      })
      .pipe(
        tap((session: SessionModelResponse) => {
          this.posthogService.capture(
            PosthogEvent.Login,
            new Login().create(session)
          );

          if (comesFromRegister) {
            this.posthogService.capture(
              PosthogEvent.Register,
              new Register().createRegister(session)
            );
          }

          this.abTestsService.setSeed(session.user.ab_test_seed);

          this.store.dispatch(new SetSession(session));

          this.cartService.refreshCart(true);

          if (GonativeService.isGonative()) {
            this.abTestsService.sendEvent('native-app-login');
          }

          this.gtm.login();

          if (this.posthogService.getDistinctId() !== String(session.user.id)) {
            this.posthogService.identify(`${session.user.id}`, {
              email: session.user.email,
              username: session.user.username,
              name: `${session.user.first_name} ${session.user.last_name}`
            });
          }
        })
      );
  }

  public registerAndLogin(
    registrationFormData: RegistrationData,
    pageKey = 'regular',
    clearBasket = false
  ): Observable<SessionModelResponse> {
    return this.register(registrationFormData, pageKey).pipe(
      mergeMap(() => {
        return this.login(
          registrationFormData.username,
          registrationFormData.plainPassword,
          clearBasket,
          true
        );
      })
    );
  }

  public register(registrationFormData: RegistrationData, pageKey = 'regular') {
    const reqParams = { pageKey: pageKey, userengageKey: GetUserEngageKey('') };

    let queryParamsString = '';
    Object.keys(reqParams).forEach(param => {
      if (reqParams[param] !== null) {
        queryParamsString += queryParamsString === '' ? '?' : '&';
        queryParamsString += param + '=' + reqParams[param];
      }
    });

    const dataToMerge = {
      acquisition: this.window['gtmTrackedCampaignSource']
        ? this.window['gtmTrackedCampaignSource']
        : null,
      acquisitionCampaign: this.window['gtmTrackedCampaignName']
        ? this.window['gtmTrackedCampaignName']
        : null,
      status: 1
    };

    const requestData = {
      ...dataToMerge,
      ...registrationFormData,
      abTestSeed: this.abTestsService.getInitiatedSeed()
    };

    return this.httpClient.post(
      API_LIST.registration() + queryParamsString,
      requestData
    );
  }

  logout(path: string, user: User) {
    this.posthogService.capture(PosthogEvent.Logout, new Logout().create(user));
    this.store.dispatch(new ClearAuthData());
    this.userEngage.resetAuth();
    this.posthogService.reset();
    this.router.navigate([path]);
  }
}
